import {Injectable} from '@angular/core';
import {WebGLVisualImpairment} from './webgl-visual-impairment';
import {
    ImpairedVisionSimulationConfig,
    ImpairedVisionSimulationName
} from './views/impaired-vision-simulation-control-view.component';
import {Observable, of, switchMap} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';


type ImpairVisionSimName = Exclude<
    ImpairedVisionSimulationName,
    "myopia" | "glare" | "peripheral" | "central" | "contrast"
>;

export type ImpairedVisionData = {
    [K in ImpairVisionSimName]: boolean
} & {
    blur: number, // unit is px
    brightness: number, // unit is percent
    contrast: number, // unit is percent
}


function toImpairedVisionData(simulations: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig>) {
    let data: ImpairedVisionData = {
        blur: 0,
        brightness: 100,
        contrast: 1.0,
        yellowing: false,
        double: false,
        protanopia: false,
        deuteranopia: false,
        tritanopia: false,
        achromatopsia: false,
    };
    if (simulations['myopia']?.enabled) {
        const config = simulations['myopia'].config!;
        data.blur = data.blur + (config.degree || 0.0) * 10;
    }
    if (simulations['glare']?.enabled) {
        const config = simulations['glare'].config!;
        data.brightness = data.brightness + (config.degree || 0.0) * 200; // 300% is the maximum
    }
    if (simulations['contrast']?.enabled) {
        const config = simulations['contrast'].config!;
        data.contrast = config.degree || 1.0;
    }
    const simulationNames: ImpairVisionSimName[] =
        ['yellowing', 'double', 'protanopia', 'deuteranopia', 'tritanopia', 'achromatopsia'];
    simulationNames.forEach((name: ImpairVisionSimName) => {
        if (simulations[name]?.enabled) {
            data[name] = true;
        }
    });
    return data;
}


export function hasActiveVisionSimulation(simulations: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig>) {
    for (const key of Object.keys(simulations)) {
        const config: ImpairedVisionSimulationConfig = simulations[key as ImpairedVisionSimulationName];
        if (config.enabled) {
            return true;
        }
    }
   return false;
}


export interface ImpairedVisionSimulationObjState {
    cleanupFunction: () => void;
    webGLObj: WebGLVisualImpairment;
}


@Injectable()
export class WebGLVisualImpairmentService {
    simulateVisionImpairments$(
        sourceCanvas: HTMLCanvasElement, targetCanvas: HTMLCanvasElement,
        simulations: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig>,
        simulationTrigger$: Observable<void>,
        existingState?: ImpairedVisionSimulationObjState,
    ): Observable<ImpairedVisionSimulationObjState> {
        targetCanvas.width = sourceCanvas.width;
        targetCanvas.height = sourceCanvas.height;
        const webGL = existingState ? existingState.webGLObj: new WebGLVisualImpairment();
        return webGL.init$(targetCanvas).pipe(
            switchMap(() => {
                return simulationTrigger$.pipe(
                    switchMap(() => {
                        webGL.updateTexture(sourceCanvas);
                        const impairmentData = toImpairedVisionData(simulations);
                        webGL.applyEffect(impairmentData);
                        const cleanupFunc = () => {
                            webGL.destroy();
                        };
                        return of({
                            cleanupFunction: cleanupFunc,
                            webGLObj: webGL
                        });
                    }),
                )
            }),
            finalize(() => {
                webGL.destroy();
            })
        );
    }
}


